import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { toast } from "sonner";
import { Shield, Trash2, Plus, Save, AlertTriangle } from "lucide-react";

const WEIGHT_FIELDS = [
  { key: "high_risk_threshold", label: "Umbral alto riesgo (auto-cancelar)", max: 100 },
  { key: "medium_risk_threshold", label: "Umbral medio riesgo (revisar)", max: 100 },
  { key: "weight_failed_3plus", label: "Peso: 3+ pagos fallidos en 24h", max: 100 },
  { key: "weight_failed_2", label: "Peso: 2 pagos fallidos en 24h", max: 100 },
  { key: "weight_velocity_3plus", label: "Peso: 3+ pedidos en 1h (velocity)", max: 100 },
  { key: "weight_non_us", label: "Peso: envío fuera de EE.UU.", max: 100 },
  { key: "weight_incomplete_addr", label: "Peso: dirección incompleta", max: 100 },
  { key: "weight_high_value_first", label: "Peso: primer pedido de alto valor", max: 100 },
  { key: "high_value_threshold", label: "Umbral \"alto valor\" ($)", max: 5000 },
  { key: "weight_disposable_email", label: "Peso: email desechable", max: 100 },
];

export default function AdminFraud() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blocklist, setBlocklist] = useState([]);
  const [newBlock, setNewBlock] = useState({ kind: "email", value: "", note: "" });
  const [domainsText, setDomainsText] = useState("");

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  const load = async () => {
    const [s, b] = await Promise.all([
      api.get("/admin/fraud/settings").then((r) => r.data),
      api.get("/admin/fraud/blocklist").then((r) => r.data),
    ]);
    setSettings(s);
    setDomainsText((s.disposable_domains || []).join("\n"));
    setBlocklist(b.items || []);
  };

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      payload.disposable_domains = domainsText
        .split(/\r?\n/)
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);
      const { data } = await api.put("/admin/fraud/settings", payload);
      setSettings(data);
      setDomainsText((data.disposable_domains || []).join("\n"));
      toast.success("Reglas guardadas");
    } catch (e) {
      toast.error("No se pudo guardar", { description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    if (!newBlock.value.trim()) return;
    try {
      const { data } = await api.post("/admin/fraud/blocklist", newBlock);
      setBlocklist((cur) => [data, ...cur]);
      setNewBlock({ kind: newBlock.kind, value: "", note: "" });
      toast.success("Añadido a la lista negra");
    } catch (e) {
      toast.error("Error", { description: e?.response?.data?.detail || String(e) });
    }
  };

  const removeBlock = async (id) => {
    if (!window.confirm("¿Eliminar de la lista negra?")) return;
    try {
      await api.delete(`/admin/fraud/blocklist/${id}`);
      setBlocklist((cur) => cur.filter((b) => b.id !== id));
      toast.success("Eliminado");
    } catch (e) {
      toast.error("Error", { description: e?.response?.data?.detail || String(e) });
    }
  };

  if (loading || !settings) return <div className="p-24 text-center">Cargando…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="admin-fraud">
      <div className="flex items-center gap-3 mb-8">
        <Shield strokeWidth={1.25} className="h-8 w-8 text-[#8B0000]" />
        <div>
          <h1 className="font-serif text-4xl">Reglas antifraude</h1>
          <p className="text-sm text-muted-foreground">Umbrales, pesos por regla y lista negra sin redeploy.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Weights */}
        <section className="border border-border p-6">
          <h2 className="font-serif text-2xl mb-4">Umbrales y pesos</h2>
          <div className="space-y-3">
            {WEIGHT_FIELDS.map((f) => (
              <label key={f.key} className="flex items-center gap-3">
                <span className="flex-1 text-sm">{f.label}</span>
                <input
                  type="number"
                  min={0}
                  max={f.max}
                  step={f.key === "high_value_threshold" ? 10 : 1}
                  value={settings[f.key] ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, [f.key]: Number(e.target.value) })
                  }
                  className="w-24 border border-border px-3 py-2 text-right"
                  data-testid={`fraud-input-${f.key}`}
                />
              </label>
            ))}
          </div>
          <div className="mt-6">
            <label className="text-sm block mb-2">Dominios de email desechable (uno por línea)</label>
            <textarea
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              rows={5}
              className="w-full border border-border p-3 font-mono text-xs"
              data-testid="fraud-disposable-domains"
            />
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="mt-6 w-full bg-navy text-cream py-3 font-mono-caps hover:bg-cream hover:text-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="fraud-save-btn"
          >
            <Save className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar reglas"}
          </button>
        </section>

        {/* Blocklist */}
        <section className="border border-border p-6">
          <h2 className="font-serif text-2xl mb-4">Lista negra</h2>
          <div className="flex gap-2 mb-3">
            <select
              value={newBlock.kind}
              onChange={(e) => setNewBlock({ ...newBlock, kind: e.target.value })}
              className="border border-border px-3 py-2"
              data-testid="blocklist-kind"
            >
              <option value="email">Email</option>
              <option value="postal">Código postal</option>
            </select>
            <input
              value={newBlock.value}
              onChange={(e) => setNewBlock({ ...newBlock, value: e.target.value })}
              placeholder={newBlock.kind === "email" ? "abuse@example.com" : "10001"}
              className="flex-1 border border-border px-3 py-2"
              data-testid="blocklist-value"
            />
          </div>
          <input
            value={newBlock.note}
            onChange={(e) => setNewBlock({ ...newBlock, note: e.target.value })}
            placeholder="Motivo (opcional)"
            className="w-full border border-border px-3 py-2 mb-3 text-sm"
            data-testid="blocklist-note"
          />
          <button
            onClick={addBlock}
            className="w-full bg-[#8B0000] text-cream py-2 font-mono-caps hover:bg-navy transition-colors flex items-center justify-center gap-2"
            data-testid="blocklist-add-btn"
          >
            <Plus className="h-4 w-4" /> Añadir
          </button>

          <ul className="mt-6 divide-y divide-border">
            {blocklist.length === 0 && (
              <li className="text-sm text-muted-foreground py-4 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Aún no hay entradas.
              </li>
            )}
            {blocklist.map((b) => (
              <li key={b.id} className="py-3 flex items-center gap-3" data-testid={`blocklist-item-${b.id}`}>
                <span className="font-mono-caps text-[10px] bg-muted px-2 py-1">
                  {b.email ? "EMAIL" : "POSTAL"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{b.email || b.postal}</div>
                  {b.note && <div className="text-xs text-muted-foreground truncate">{b.note}</div>}
                </div>
                <button
                  onClick={() => removeBlock(b.id)}
                  className="text-muted-foreground hover:text-[#8B0000]"
                  aria-label="Eliminar"
                  data-testid={`blocklist-remove-${b.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
